


























































































































import { Component, Ref, Vue } from "vue-property-decorator";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import AdminRefreshButtonComponent from "@/components/admin/AdminRefreshButton.vue";
import MiraCompanyDialogComponent from "@/components/admin/mira/MiraCompanyDialog.vue";
import { ClientAdministrationRow } from "@/data/models/ClientAdministrationRow";
import { MiraCompany } from "@/data/models/MiraCompanyAdministration";
import { listClientsForAdministration } from "@/data/repository/ClientAdministrationRepository";
import { listMiraCompanies } from "@/data/repository/MiraCompanyAdministrationRepository";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { getValue, setValue } from "@/utils/SecureLocalStorage";
import { getLastVisitedItem, LAST_VISITED_ITEM_BACKGROUND_STYLE, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { clientLabel, coveredClients, filterCompanies, MIRA_COMPANY_FILTERS, MiraCompanyFilter } from "@/utils/MiraCompanyAdministration";
import { themed } from "@/utils/Theme";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/**
 * The companies Mira's market comparison counts as one (#2610009 C4): a company that runs several
 * Mandanten (BELFOR's, POLYGON's branches) is one value in the Markt, computed over all of them, not one
 * per Mandant. A row opens the company to view and edit; the round plus adds one. An edit reaches Mira
 * with the next nightly run.
 *
 * **Not the access boundary**: `listMiraCompaniesForAdministration` repeats the portal and
 * `MANAGE_CLIENTS` checks, as every save does.
 */
const DEFAULT_FILTER: MiraCompanyFilter = "ACTIVE";
/** Session storage key (see `SecureLocalStorage`) for the list's filter. */
const FILTER_STORAGE_KEY = "ADMIN_MIRA_COMPANIES_FILTER";
/** How many Mandanten a row names before "und n weitere". */
const SUMMARY_CLIENTS = 3;

@Component({
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "content-state": ContentStateComponent,
    "admin-refresh-button": AdminRefreshButtonComponent,
    "company-dialog": MiraCompanyDialogComponent,
  },
})
export default class MiraCompanyListComponent extends Vue {
  @Ref("companyDialog")
  companyDialog!: MiraCompanyDialogComponent;

  companies: MiraCompany[] = [];
  clients: ClientAdministrationRow[] = [];
  filter: MiraCompanyFilter = DEFAULT_FILTER;
  search = "";
  isLoading = true;
  loadError = false;
  lastVisitedId: string | null = null;

  readonly filters = MIRA_COMPANY_FILTERS;
  readonly defaultFilter = DEFAULT_FILTER;

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  get clientsById(): Record<string, ClientAdministrationRow> {
    return Object.fromEntries(this.clients.map((client) => [client.id, client]));
  }

  get rows(): MiraCompany[] {
    return filterCompanies(this.companies, this.filter, this.search, (clientId) => this.labelOf(clientId));
  }

  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  labelOf(clientId: string): string {
    return clientLabel(this.clientsById[clientId], clientId);
  }

  /** Every Mandant the company covers, a group's members included. */
  covered(row: MiraCompany): string[] {
    return coveredClients(row);
  }

  /** The first Mandanten by name, then how many more. */
  clientSummary(row: MiraCompany): string {
    const labels = coveredClients(row).map((clientId) => this.labelOf(clientId)).sort((a, b) => a.localeCompare(b, "de"));
    const shown = labels.slice(0, SUMMARY_CLIENTS).join(", ");
    return labels.length > SUMMARY_CLIENTS
      ? this.$t("admin.miraCompanies.moreClients", [shown, labels.length - SUMMARY_CLIENTS]).toString()
      : shown;
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(row: MiraCompany, hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    if (background) {
      return `background-color: ${background}`;
    }
    return row.id === this.lastVisitedId ? LAST_VISITED_ITEM_BACKGROUND_STYLE : "";
  }

  filterName(filter: MiraCompanyFilter): string {
    return this.$t(`admin.miraCompanies.filters.${filter}`).toString();
  }

  onFilterChanged(filter: MiraCompanyFilter) {
    this.filter = filter;
    setValue(FILTER_STORAGE_KEY, filter);
  }

  onSearch(value: string | null) {
    this.search = value ?? "";
  }

  async open(row: MiraCompany | null) {
    if (row) {
      this.lastVisitedId = row.id;
      setLastVisitedItem(row.id, StorageKeyLastVisitedItemLevel.LEVEL_1);
    }
    const result = await this.companyDialog.show(row, this.clients, this.companies);
    if (result === "CHANGED_MEANWHILE") {
      this.refreshCompanies();
    } else if (result) {
      this.lastVisitedId = result.id;
      setLastVisitedItem(result.id, StorageKeyLastVisitedItemLevel.LEVEL_1);
      // Read again rather than patched: only the list carries what a company covers through its groups.
      this.refreshCompanies();
    }
  }

  /**
   * After a save, or a refusal as changed meanwhile: reads the companies again in the background, without
   * the page's loading state, so the list stays where it is (Taimoor, 2026-10-20: no full reload after a
   * save). The Mandanten do not change through a company, so they are not read again.
   */
  async refreshCompanies() {
    try {
      this.companies = await listMiraCompanies();
    } catch {
      // Swallowed on purpose: the list keeps what it showed, and the refresh button reloads on demand.
    }
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;
    try {
      const [companies, clients] = await Promise.all([listMiraCompanies(), listClientsForAdministration()]);
      this.companies = companies;
      this.clients = clients.clients;
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button retries.
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }

  mounted() {
    ToolbarContentModule.setI18nResource("admin.miraCompanies.title");
    const stored = getValue(FILTER_STORAGE_KEY);
    this.filter = MIRA_COMPANY_FILTERS.find((filter) => filter === stored) ?? DEFAULT_FILTER;
    this.lastVisitedId = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.load();
  }
}
